
import React, { useState, useEffect, useMemo } from 'react';
import { SubscriptionPlan } from '../../../types';
import Modal from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import QuantitySelector from '../../../components/ui/QuantitySelector';

interface SubscriptionSelectionModalProps {
    plan: SubscriptionPlan | null;
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (planId: string, userCount: number) => void;
    billingCycle: 'monthly' | 'annually';
    currentUserCount: number;
    minUserCount?: number;
    isLoading?: boolean;
}

const SubscriptionSelectionModal: React.FC<SubscriptionSelectionModalProps> = ({ plan, isOpen, onClose, onConfirm, billingCycle, currentUserCount, minUserCount = 1, isLoading = false }) => {
    const [userCount, setUserCount] = useState(Math.max(currentUserCount, minUserCount));

    useEffect(() => {
        if (isOpen) {
            setUserCount(Math.max(currentUserCount, minUserCount));
        }
    }, [isOpen, currentUserCount, minUserCount]);

    const pricePerUser = useMemo(() => {
        if (!plan) return 0;
        return parseFloat(plan.price.replace(',', '.'));
    }, [plan]);

    const totalPrice = useMemo(() => {
        const total = userCount * pricePerUser;
        return billingCycle === 'annually' ? total * 10 : total;
    }, [userCount, pricePerUser, billingCycle]);

    const handleConfirm = () => {
        if (plan && !isLoading) {
            onConfirm(plan.id, userCount);
        }
    };

    if (!isOpen || !plan) return null;

    const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(totalPrice);

    const cycleLabel = billingCycle === 'monthly' ? 'mensal' : 'anual';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            preventBackdropClose={isLoading}
            overlayClassName="max-md:items-end max-md:p-0 md:items-center md:p-4"
            className="max-md:max-h-[min(92dvh,680px)] max-md:w-full max-md:rounded-t-2xl max-md:rounded-b-none max-md:animate-in max-md:slide-in-from-bottom md:max-w-md md:rounded-lg"
        >
            <div className="max-md:flex max-md:justify-center max-md:pt-3 max-md:pb-1 md:hidden" aria-hidden="true">
                <span className="h-1 w-10 rounded-full bg-border-strong" />
            </div>
            <Modal.Header onClose={onClose} showCloseButton={!isLoading} className="max-md:px-5 max-md:py-3.5">
                <span className="text-body-md md:text-heading-sm text-default font-semibold">Plano {plan.name}</span>
            </Modal.Header>
            <Modal.Body className="max-md:px-5 max-md:py-4 max-md:space-y-5 md:space-y-4">
                <p className="text-body-sm text-secondary text-center max-md:leading-snug">
                    Quantos usuários você precisa?
                </p>

                <div className="flex flex-col items-center justify-center max-md:py-2">
                    <label htmlFor="subscription-user-count" className="sr-only">
                        Quantidade de usuários
                    </label>
                    <QuantitySelector
                        value={userCount}
                        onChange={setUserCount}
                        min={minUserCount}
                        disabled={isLoading}
                        className="justify-center max-md:gap-6 md:gap-4"
                    />
                </div>

                <section
                    aria-label="Total estimado"
                    className="text-center bg-surface border border-subtle max-md:p-4 md:p-6 rounded-xl md:rounded-2xl"
                >
                    <p className="text-caption md:text-body-sm font-semibold text-muted mb-0.5">Total estimado · {cycleLabel}</p>
                    <p className="text-heading-lg md:text-4xl font-extrabold text-text tracking-tight">{formattedPrice}</p>
                    <p className="text-caption text-muted mt-0.5">
                        {userCount} {userCount === 1 ? 'usuário' : 'usuários'} × R$ {pricePerUser.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                </section>
            </Modal.Body>
            <Modal.Footer className="max-md:flex-col-reverse max-md:gap-2.5 max-md:px-5 max-md:py-4 max-md:pb-[max(1rem,env(safe-area-inset-bottom))] max-md:[&>button]:w-full max-md:[&>button]:min-h-12">
                <Button
                    variant="ghost"
                    size="lg"
                    className="rounded-xl font-semibold border border-subtle hover:bg-surface text-secondary"
                    onClick={onClose}
                    disabled={isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    size="lg"
                    className="rounded-xl bg-primary hover:bg-primary-dark text-primary-foreground font-semibold shadow-lg"
                    onClick={handleConfirm}
                    disabled={isLoading}
                >
                    {isLoading ? 'Redirecionando...' : 'Confirmar Assinatura'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SubscriptionSelectionModal;